import * as yup from 'yup'
import { noodlesAddressSchema, noodlesRemittanceInstructionsSchema } from './noodles-order.js'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesCreditNoteLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const noodlesCreditNoteFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  shippingMethod: yup.string().nullable().optional(),
  referenceNumber: yup.string().nullable().optional(),
  invoiceNumber: yup.string().nullable().optional(),
  creditNumber: yup.string().nullable().optional(),
  paymentMethod: yup.string().nullable().optional(),
  date: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  customer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),

        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  showBankDetails: yup.string().nullable().optional(),
  isNet: yup.string().nullable().optional(),
})

export type NoodlesCreditNoteFileData = yup.InferType<typeof noodlesCreditNoteFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesCreditNoteFileData>

export const noodlesCreditNoteFileMappings: RequiredFileData = {
  issuer: {
    companyName: 'a0cbcf09-8ce8-40fe-b4d0-8584bf82284d',
    address: {
      addressLine1: '31919fda-739a-4a37-953f-10f924878cd6',
      addressLine2: 'b1368702-833b-4cb6-b04d-2a0338ec27b3',
      postalCode: '16be1e44-6d0c-4649-b560-34696ee0cb87',
      city: '9016afac-c403-451d-afbf-193a6ed65f6a',
      country: '89c49eb4-7892-4e28-9333-1c7c6f62bb48',
    },
    firstName: '00eb1d27-9795-4d0c-b739-3bc25e872128',
    lastName: 'b2b6cea5-ae4a-4227-bc55-812022a3cfac',
    phoneNumber: '45cadf31-9aec-41b2-8d79-3cdcdcfd8f75',
    emailAddress: '5ec2c7d3-92c0-4297-9fd4-c0380e3255b4',
  },
  shippingMethod: 'c9382398-3092-4ee6-a3b9-320793de5533',
  referenceNumber: 'c4670394-ca44-4ff0-b01d-4e6b65f0c378',
  invoiceNumber: '86608d0b-9430-4c70-9753-0a6dce161c92',
  creditNumber: 'd56aad84-56f0-490d-83b8-e87278152647',
  paymentMethod: 'a62306bc-ae9f-4c5c-b028-baf54810d804',
  date: '13ed86ca-bed8-4f68-b9d4-05c5904cde69',
  externalNote: '1e85deea-7478-4af8-b8b0-a9c227a07158',
  customer: {
    companyName: '7128eba8-ef3f-42da-8895-380431e93beb',
    address: {
      addressLine1: '018fcafa-f428-45c5-bd50-d36f5a5f8298',
      addressLine2: 'd1fb9b8b-d5df-498f-875d-e57a04e58f86',
      postalCode: '31234f3b-9deb-458c-ba61-ae374824ab66',
      city: '27c5530b-d5a8-4fa4-9163-1e41eec10bf2',
      country: '26280742-ffbb-41ef-993e-589c1d03dbea',
    },
    firstName: 'c6f5f087-2eea-4e58-8f86-fe2f60807f8e',
    lastName: '6f972e94-e2f9-439a-9697-a4fb902e599d',
    phoneNumber: '63850994-2f83-429a-a774-024018148d67',
    emailAddress: '745583ed-e23a-44fc-affa-40d76d41b305',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'e8a8984e-3aca-4840-bfc1-d53d9a8f93f7',
      description: '5f662a67-0b51-446e-b42d-52c70f634dc1',
      itemQty: '984e4b44-dba2-4ce4-9bd7-ed898e19c36f',
      unitPrice: 'a68a3a99-3f3f-4bf1-af9e-32bfca94c592',

      lineTotal: null,
    },
  ],
  taxRate: '366e94f8-7f31-4040-b64d-3e9a5e279234',
  agreement: 'a24d3338-fae5-4eb3-9533-2596a0281787',
  instructions: '3c9ebe9b-7078-4623-a4cf-0290ff821b1b',
  remittanceInstructions: {
    bankName: '5ed63e6a-12ce-4151-b4ee-8d718c8c98fb',
    bankAddress: {
      addressLine1: 'ae32decc-d14f-49a2-b7b2-83ae87b7af3f',
      addressLine2: '7d91a521-f793-4de8-9f8c-d9176289678f',
      postalCode: '3aec6931-012a-4309-bc65-b477b376cfd1',
      city: '5f1b4228-15f3-4769-8091-63395d193775',
      country: '1414d1e3-5e26-4dad-9d8b-4ba9ca6f3834',
    },
    kto: 'f134917e-ae58-43ed-bace-6fdf4da0b943',
    blz: '8cad5fe9-3de3-490c-8a39-f0ea22ea8ff7',
    iban: '4d7dcc3d-cfa8-46fa-95af-2523928fcef6',
    swift: 'a2225269-2ce7-4fd7-8951-c68406c246ed',
    firstName: '4f0a1bdb-15fb-4a0d-a5a6-5c0d83104d79',
    lastName: '300e9f75-f057-4889-97e2-14794335b2c4',
    ustIdNr: 'a1a5e616-77f6-489b-8081-0fe402216b25',
    beneficiaryName: '423c11cf-a3da-42e5-b834-3f1d2080ac70',
    beneficiaryAddress: {
      addressLine1: '506f6cf7-1574-4033-bd62-b1923f75b181',
      addressLine2: '2706b3da-e7b1-4ca4-a9d8-b3c50fdb86f6',
      postalCode: '748d85f3-5b21-4e30-b5db-3e111044d73b',
      city: 'd7432ce0-5a98-4f6a-98c5-92eef5b4a789',
      country: '892331a0-bded-490b-b531-026235df6ed6',
    },
    telephone: 'b1b8ac99-3cf2-49bd-8911-3a8391615d4b',
    telefax: 'be4213b1-4f23-4d58-b03b-2ac303142dc9',
    email: '732dd52f-0d78-449a-b3da-84455b20f86b',
    website: '384b9425-7660-4a95-af61-afa33fa5980c',
    paymentLink: '5d4d39a7-6b1d-4874-adb1-54eaecbc67d0',
  },
  showBankDetails: '15f8507f-8749-4579-9677-914700f45e44',
  isNet: '6e489859-b385-4b04-be3d-80ca5aa17e40',
}
